// frontend/src/components/ImageResizer.js
//
// Client-side image resizer for the size/dimension pages.
//
// WHY A TOOL AND NOT A GUIDE. Measured 2026-08-16: every size keyword carries an
// AI Overview — `instagram post size dimensions` (33,100/mo), `youtube thumbnail
// size` (33,100), `facebook cover photo size` (12,100), `linkedin banner size`
// (8,100), `instagram story size` (8,100), `instagram carousel size` (1,600).
// "What size is X" is the perfect AI Overview query: the answer is a number and
// Google states it, so a size *guide* loses the click before it starts.
//
// But an AI Overview can tell you 1080x1350 and cannot resize your image. That
// job survives, and resizer tools do still rank underneath the overview —
// lightxeditor, adobe/express/feature/image/resize, simpleimageresizer,
// overvisual all hold positions. So the tool is the format that works.
//
// THE LINK WALLS ARE LOW where it matters: median referring domains across the
// top four is 13 for `instagram post size dimensions`, 14 for story size, and 1
// for carousel size. Our own /blog/instagram-reels-dimensions ranks with zero.
//
// EVERYTHING HAPPENS IN THE BROWSER. Canvas only — the image is never uploaded,
// which is both a real privacy difference from the server-side resizers on this
// SERP and the reason this costs nothing to run.

import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Row, Col, Button, Form, Badge } from 'react-bootstrap'
import { CloudArrowUp, Download, ShieldCheck, ArrowRepeat } from 'react-bootstrap-icons'

/** Draw `img` into w×h, cropping from the centre so the frame is filled (no bars). */
function drawCover (img, w, h) {
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  const scale = Math.max(w / img.width, h / img.height)
  const sw = w / scale
  const sh = h / scale
  const sx = (img.width - sw) / 2
  const sy = (img.height - sh) / 2
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h)
  return canvas
}

/** Draw `img` into w×h whole, padding with a colour so nothing is cropped away. */
function drawFit (img, w, h, bg) {
  const canvas = document.createElement('canvas')
  canvas.width = w; canvas.height = h
  const ctx = canvas.getContext('2d')
  ctx.imageSmoothingQuality = 'high'
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, w, h)
  const scale = Math.min(w / img.width, h / img.height)
  const dw = img.width * scale
  const dh = img.height * scale
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
  return canvas
}

export default function ImageResizer ({ formats, platformLabel = 'Instagram' }) {
  const [img, setImg]         = useState(null)   // HTMLImageElement
  const [fileName, setName]   = useState('')
  const [mode, setMode]       = useState('cover')
  const [bg, setBg]           = useState('#ffffff')
  const [outputs, setOutputs] = useState([])     // [{key,label,w,h,url,bytes}]
  const [busy, setBusy]       = useState(false)
  const [error, setError]     = useState('')
  const inputRef = useRef(null)
  const urlsRef  = useRef([])

  // Object URLs are leaked memory until revoked; clear them on every regeneration
  // and on unmount.
  const revoke = () => { urlsRef.current.forEach(u => URL.revokeObjectURL(u)); urlsRef.current = [] }
  useEffect(() => revoke, [])

  const load = file => {
    if (!file) return
    if (!/^image\//.test(file.type)) { setError('That file is not an image.'); return }
    setError('')
    const reader = new FileReader()
    reader.onload = e => {
      const image = new Image()
      image.onload = () => { setImg(image); setName(file.name.replace(/\.[^.]+$/, '')) }
      image.onerror = () => setError('Could not read that image. Try a JPG or PNG.')
      image.src = e.target.result
    }
    reader.onerror = () => setError('Could not read that file.')
    reader.readAsDataURL(file)
  }

  const generate = useCallback(async () => {
    if (!img) return
    setBusy(true); revoke()
    const made = []
    for (const f of formats) {
      const canvas = mode === 'cover' ? drawCover(img, f.w, f.h) : drawFit(img, f.w, f.h, bg)
      // eslint-disable-next-line no-await-in-loop
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92))
      if (!blob) continue
      const url = URL.createObjectURL(blob)
      urlsRef.current.push(url)
      made.push({ ...f, url, bytes: blob.size })
    }
    setOutputs(made); setBusy(false)
  }, [img, mode, bg, formats])

  // Regenerate whenever the image or the fit mode changes.
  useEffect(() => { if (img) generate() }, [img, mode, bg, generate])

  const reset = () => { revoke(); setImg(null); setOutputs([]); setName(''); setError('') }

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>Resize your image for {platformLabel}</h2>
      <p className='text-muted mb-4'>
        Drop an image in and get every {platformLabel} size at once, correctly cropped.
        <strong> Nothing is uploaded</strong> — the resizing happens in your browser.
      </p>

      {!img && (
        <div
          onDragOver={e => { e.preventDefault() }}
          onDrop={e => { e.preventDefault(); load(e.dataTransfer.files?.[0]) }}
          onClick={() => inputRef.current?.click()}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
          role='button' tabIndex={0}
          className='text-center p-5 rounded-3'
          style={{ border: '2px dashed #cbd5e1', background: '#f8fafc', cursor: 'pointer' }}
        >
          <CloudArrowUp size={40} className='text-primary mb-2' />
          <div className='fw-semibold'>Drop an image here, or click to choose one</div>
          <div className='text-muted small mt-1'>JPG, PNG or WebP · never leaves your device</div>
          <input ref={inputRef} type='file' accept='image/*' hidden
                 onChange={e => load(e.target.files?.[0])} />
        </div>
      )}

      {error && <div className='text-danger small mt-3'>{error}</div>}

      {img && (
        <>
          <Row className='g-3 align-items-end mb-4'>
            <Col md={4}>
              <Form.Label className='fw-semibold small'>Your image</Form.Label>
              <div className='text-muted small'>
                {img.width} × {img.height}px
              </div>
            </Col>
            <Col md={4}>
              <Form.Label className='fw-semibold small'>How should it fit?</Form.Label>
              <Form.Select value={mode} onChange={e => setMode(e.target.value)}>
                <option value='cover'>Crop to fill (recommended)</option>
                <option value='fit'>Fit whole image, add background</option>
              </Form.Select>
            </Col>
            <Col md={4}>
              {mode === 'fit' ? (
                <>
                  <Form.Label className='fw-semibold small'>Background</Form.Label>
                  <Form.Control type='color' value={bg} onChange={e => setBg(e.target.value)}
                                title='Background colour' style={{ height: 38 }} />
                </>
              ) : (
                <Button variant='outline-secondary' onClick={reset}>
                  <ArrowRepeat className='me-1' />Use a different image
                </Button>
              )}
            </Col>
          </Row>

          {busy && <p className='text-muted small'>Resizing…</p>}

          <Row className='g-3'>
            {outputs.map(o => (
              <Col sm={6} lg={4} key={o.key}>
                <div className='p-3 rounded-3 h-100 d-flex flex-column' style={{ border: '1px solid #e5e7eb' }}>
                  <div className='mb-2 d-flex align-items-center justify-content-between'>
                    <span className='fw-semibold small'>{o.label}</span>
                    {o.recommended && <Badge bg='primary' className='fw-normal'>Recommended</Badge>}
                  </div>
                  <div className='mb-2 d-flex align-items-center justify-content-center rounded position-relative'
                       style={{ background: '#f1f5f9', minHeight: 128 }}>
                    <span className='position-relative d-inline-block'>
                      <img src={o.url} alt={`${o.label} preview`}
                           style={{ maxWidth: '100%', maxHeight: 128, display: 'block' }} />
                      {/* Safe zone overlay — preview only, never burned into the download.
                          A YouTube banner is uploaded at 2560x1440 but only the centre
                          1546x423 is guaranteed to show on every device, which is the
                          single thing people get wrong and no competing resizer shows. */}
                      {o.safe && (
                        <span
                          aria-hidden='true'
                          className='position-absolute'
                          style={{
                            top: `${(1 - o.safe.h / o.h) * 50}%`,
                            left: `${(1 - o.safe.w / o.w) * 50}%`,
                            width: `${(o.safe.w / o.w) * 100}%`,
                            height: `${(o.safe.h / o.h) * 100}%`,
                            border: '2px solid rgba(37,99,235,.95)',
                            boxShadow: '0 0 0 9999px rgba(15,23,42,.42)',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </span>
                  </div>
                  {o.safe && (
                    <div className='small text-primary mb-1'>
                      Boxed area = the {o.safe.w} × {o.safe.h} safe zone, all that shows on every device
                    </div>
                  )}
                  <div className='text-muted small mb-2' style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {o.w} × {o.h}px · {o.ratio} · {Math.round(o.bytes / 1024)} KB
                  </div>
                  <Button size='sm' variant='outline-primary' className='mt-auto'
                          href={o.url} download={`${fileName || 'image'}-${o.w}x${o.h}.jpg`}>
                    <Download className='me-1' />Download
                  </Button>
                </div>
              </Col>
            ))}
          </Row>

          {mode === 'fit' && (
            <Button variant='outline-secondary' size='sm' className='mt-3' onClick={reset}>
              <ArrowRepeat className='me-1' />Use a different image
            </Button>
          )}

          <p className='text-muted small mt-3 mb-0'>
            <ShieldCheck className='me-1 text-success' />
            Your image never left this page — no upload, no server, nothing stored.
          </p>

          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>Skip this step entirely</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              At Greatly Social you can crop to each network's ratio inside the post editor, then
              schedule it — instead of resizing, downloading and re-uploading for every platform.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' href='/video-photo-editor' className='mb-2'>
              See the editor
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
